import { SearchRegistry } from '../types';

export const platformStatusSearchEntries: SearchRegistry = {
  '/platform-status-and-alerts/overview': {
    keywords: [
      'platform status',
      'status overview',
      'system status',
      'platform health',
      'status page',
      'service status',
      'health overview',
      'platform status & alerts',
      'platform status and alerts',
      'status landing',
      'incident summary',
    ],
    questions: [
      'Is ryd up right now?',
      'Where is the platform status overview?',
      'How do I see if there are any active incidents?',
      'Where do I find scheduled maintenance for the next few days?',
    ],
    faqs: [
      {
        q: 'Where do I check whether ryd is currently having issues?',
        a: 'Open Platform Status & Alerts → Overview. The page summarises the current state of every public surface and flags active incidents, scheduled maintenance and recent alerts.',
      },
    ],
  },
  '/platform-status-and-alerts/operational-excellence': {
    keywords: [
      'operational excellence',
      'operational excellence observer',
      'order count alert',
      'failed requests alert',
      'order success rate alert',
      'partner alert configuration',
      'partner slack alerts',
      'alert thresholds',
      'error rate threshold',
      'dedupe alerts',
      'quiet hours',
      'graylog partner traffic',
    ],
    questions: [
      'How do I change the order-count threshold of a partner?',
      'How do I send the Operational Excellence alerts to a partner Slack channel?',
      'How do I pause or test an Operational Excellence alert?',
    ],
  },
  '/platform-status-and-alerts/operational-alerts': {
    keywords: [
      'operational alerts',
      'my operational alerts',
      'order volume alert',
      'order success rate',
      'api request success rate',
      'alert thresholds',
      'check history',
      'alert configuration history',
      'partner alert settings',
    ],
    questions: [
      'How do I change when I get an alert about my orders or API requests?',
      'Where can I see the history of my operational alert checks?',
      'How do I go back to an earlier alert configuration?',
    ],
    faqs: [
      {
        q: 'Who can change my operational alert settings?',
        a: 'Users with the Operational Alerts Admin role can change the thresholds, pauses, repeat protection, quiet hours and portal alerts under Platform Status & Alerts → Operational Alerts. The Slack channel is managed by ryd; contact your account manager to change it.',
      },
    ],
  },
  '/platform-status-and-alerts/operational-excellence/history': {
    keywords: [
      'alert history',
      'check history',
      'configuration history',
      'operational excellence history',
      'alert versions',
      'restore alert settings',
    ],
    questions: [
      'Which Operational Excellence checks ran for a partner, and what did they find?',
      'Who changed the alert settings of a partner, and how do I restore an earlier version?',
    ],
  },
  '/platform-status-and-alerts/operational-excellence/new': {
    keywords: [
      'add monitored partner',
      'operational excellence new partner',
      'monitor api partner',
      'partner order alerts setup',
      'partner slack alert setup',
      'add partner alert',
    ],
    questions: [
      'How do I set up Operational Excellence alerts for a new API partner?',
      'How do I add a partner to the order-count and failed-request monitoring?',
    ],
  },
  '/platform-status-and-alerts/alert-slack-channels': {
    keywords: [
      'alert slack channels',
      'slack channels',
      'partner slack',
      'alert routing',
      'slack integration',
      'partner notifications',
      'slack webhook',
      'channel mapping',
      'alert delivery channels',
      'subscribe slack channel',
    ],
    questions: [
      'Which Slack channels receive ryd alerts?',
      'How do I subscribe a Slack channel to platform alerts?',
      'How do I test that a Slack channel mapping works?',
    ],
  },
  '/platform-status-and-alerts/alerts': {
    keywords: [
      'alerts',
      'platform alerts',
      'alert list',
      'partner alerts',
      'recent alerts',
      'alert history',
      'alerts feed',
      'unread alerts',
      'severity',
      'alert severity',
    ],
    questions: [
      'Where do I see the list of platform alerts?',
      'How do I read recent alerts as an API or fleet partner?',
      'How do I filter alerts by severity?',
    ],
  },
  '/platform-status-and-alerts/incidents': {
    keywords: [
      'incidents',
      'platform incidents',
      'incident list',
      'incident history',
      'outage',
      'incident timeline',
      'incident severity',
      'incident status',
      'incident updates',
      'postmortem',
      'rca',
    ],
    questions: [
      'Where do I see the list of incidents?',
      'How do I read the incident timeline for last week?',
      'Where is the post-incident review for incident X?',
    ],
  },
  '/platform-status-and-alerts/scheduled-maintenance': {
    keywords: [
      'scheduled maintenance',
      'maintenance window',
      'planned downtime',
      'maintenance list',
      'upcoming maintenance',
      'maintenance schedule',
      'maintenance announcement',
      'scheduled downtime',
    ],
    questions: [
      'When is the next scheduled maintenance?',
      'Where do I see upcoming maintenance windows?',
      'How long is the next maintenance window?',
    ],
  },
  '/platform-status-and-alerts/announcements': {
    keywords: [
      'announcements',
      'platform announcements',
      'platform news',
      'product update',
      'api change',
      'deprecation',
      'breaking change',
      'end of life',
      'policy change',
      'pricing change',
      'support notice',
      'support hours',
      'holiday coverage',
      'release notes',
      'what changed',
    ],
    questions: [
      'Where do I see platform announcements from ryd?',
      'Is anything being deprecated and by when?',
      'Where are product updates and API changes announced?',
      'Have the support hours changed over the holidays?',
    ],
    faqs: [
      {
        q: 'What is the difference between an announcement and an incident?',
        a: 'An incident records a problem on the platform. An announcement is planned news that is not a problem: product updates, API changes, deprecations, policy or support notices. Both are published under Platform Status & Alerts; announcements carry an effective date and, where you must act, an action deadline.',
      },
    ],
  },
  '/platform-status-and-alerts/admin/announcements/list': {
    keywords: [
      'admin announcements',
      'manage announcements',
      'publish announcement',
      'archive announcement',
      'announcement list',
      'draft announcement',
      'notify partners announcement',
      'deprecation notice',
    ],
    questions: [
      'Where do I manage platform announcements?',
      'How do I publish a draft announcement?',
      'How do I notify partners about a deprecation?',
    ],
  },
  '/platform-status-and-alerts/admin/announcements/create': {
    keywords: [
      'create announcement',
      'new announcement',
      'write announcement',
      'announce api change',
      'announce deprecation',
      'announce policy change',
      'announcement form',
    ],
    questions: ['How do I write and publish a new platform announcement?'],
  },
  '/platform-status-and-alerts/admin/alerts/list': {
    keywords: [
      'admin alerts',
      'alert list admin',
      'manage alerts',
      'alerts admin',
      'platform-communications-admin',
      'edit alerts',
      'platform_communications_admin',
      'alert governance',
      'alert lifecycle',
    ],
    questions: [
      'Where do I manage published alerts as an admin?',
      'How do I edit an alert as a platform-communications admin?',
    ],
  },
  '/platform-status-and-alerts/admin/alerts/create': {
    keywords: [
      'create alert',
      'new alert',
      'publish alert',
      'add alert',
      'alert creation',
      'compose alert',
      'broadcast alert',
      'alert form',
    ],
    questions: [
      'How do I publish a new platform alert?',
      'Where do I create a new alert as an admin?',
      'Which fields are required when publishing an alert?',
    ],
  },
  '/platform-status-and-alerts/admin/incidents/list': {
    keywords: [
      'admin incidents',
      'manage incidents',
      'incident admin',
      'edit incident',
      'incident list admin',
      'incident lifecycle',
      'incident status update',
      'close incident',
    ],
    questions: [
      'Where do I manage incidents as an admin?',
      'How do I edit an incident I previously published?',
      'How do I close a resolved incident?',
    ],
  },
  '/platform-status-and-alerts/admin/incidents/create': {
    keywords: [
      'create incident',
      'publish incident',
      'declare incident',
      'new incident',
      'incident creation',
      'open incident',
      'incident severity selection',
    ],
    questions: [
      'How do I declare a new incident on the platform status?',
      'Where do I create an incident as an admin?',
    ],
  },
  '/platform-status-and-alerts/admin/scheduled-maintenance/list': {
    keywords: [
      'admin scheduled maintenance',
      'manage maintenance',
      'maintenance admin',
      'edit maintenance',
      'maintenance list admin',
      'reschedule maintenance',
      'maintenance lifecycle',
    ],
    questions: [
      'Where do I manage scheduled maintenance windows as an admin?',
      'How do I update an existing maintenance window?',
      'How do I reschedule a planned maintenance?',
    ],
  },
  '/platform-status-and-alerts/admin/scheduled-maintenance/create': {
    keywords: [
      'create scheduled maintenance',
      'publish maintenance',
      'plan maintenance',
      'add maintenance window',
      'announce downtime',
      'maintenance form',
    ],
    questions: ['How do I plan and announce a new maintenance window?'],
  },
  '/platform-status-and-alerts/current-status': {
    keywords: [
      'current status',
      'live status',
      'real-time status',
      'service status',
      'health check',
      'realtime',
      'service indicators',
      'green amber red',
      'live indicators',
    ],
    questions: [
      'What is the current status of each ryd service?',
      'Where do I see the live health of the platform?',
    ],
  },
  '/platform-status-and-alerts/system-metrics': {
    keywords: [
      'system metrics',
      'platform system metrics',
      'platform metrics',
      'status metrics',
      'service metrics',
      'uptime metrics',
      'latency metrics',
      'system health metrics',
      'platform performance',
    ],
    questions: [
      'Where do I see platform system metrics?',
      'How do I review platform performance and health metrics?',
    ],
  },
  '/platform-status-and-alerts/admin/maintenance-auto-transition': {
    keywords: [
      'maintenance auto-transition',
      'auto transition',
      'automatic transitions',
      'maintenance automation',
      'auto resolve',
      'auto start maintenance',
      'auto end maintenance',
      'state transition rules',
    ],
    questions: [
      'How does maintenance auto-transition work?',
      'Where do I configure the automatic state transitions for maintenance?',
    ],
  },
  '/platform-status-and-alerts/admin/delivery-history': {
    keywords: [
      'delivery history',
      'alert delivery',
      'notification delivery',
      'delivery logs',
      'delivery audit',
      'channel delivery',
      'slack delivery',
      'email delivery audit',
    ],
    questions: [
      'Where do I see the delivery history of alerts and incidents?',
      'How do I check whether a Slack alert was delivered?',
    ],
  },
};
